import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

interface NavItem {
  label: string;
  to: string;
  match: string;
}

const navItems: NavItem[] = [
  { label: "首页", to: "/", match: "/" },
  { label: "赛事分类", to: "/category/season", match: "/category" },
  { label: "赛程", to: "/schedule/2025-26", match: "/schedule" },
  { label: "直播", to: "/live/m-live-01", match: "/live" },
  { label: "新闻", to: "/news", match: "/news" },
  { label: "关于", to: "/about", match: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="h-16 md:h-[72px] flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-xl">
              <i className="ri-football-fill" aria-hidden="true"></i>
            </span>
            <span className="leading-none">
              <span
                className={`block font-heading text-lg md:text-xl font-bold tracking-wide ${
                  transparent ? "text-background-50" : "text-foreground-950"
                }`}
              >
                欧冠直播
              </span>
              <span
                className={`block text-[10px] md:text-[11px] tracking-[0.22em] uppercase ${
                  transparent ? "text-background-200" : "text-foreground-500"
                }`}
              >
                UCL LIVE
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.match === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    transparent
                      ? "text-background-100 hover:text-background-50 hover:bg-white/10"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50"
                  } ${isActive && !transparent ? "text-primary-600 bg-primary-50" : ""} ${
                    isActive && transparent ? "text-background-50" : ""
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/live/m-live-01"
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-live-line" aria-hidden="true"></i>
              观看直播
            </Link>
          </div>

          <button
            type="button"
            aria-label="打开导航菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md text-2xl transition-colors ${
              transparent
                ? "text-background-50 hover:bg-white/10"
                : "text-foreground-800 hover:bg-background-100"
            }`}
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.match === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-base font-medium transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-800 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/live/m-live-01"
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-4 py-3 text-base font-semibold text-background-50"
            >
              <i className="ri-live-line" aria-hidden="true"></i>
              观看直播
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}